import React, { useCallback, useContext, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { getStoreBalance, getIsOrganizerShareAdded, getStoreInitBalance } from "@selectors/store";
import { setStoreBalance } from "@slices/store";

import { getStoreOpenOrderNumberKey } from "@helpers/store";
import { useWindowStorage } from "@hooks/use-window-storage";
import { getCartOrder, getIsRejectionProcessingCompleted } from "@selectors/cart";
import { ORDER_STATE_CART } from "@constants/orders/cart";

export const OpenOrderContext = React.createContext();

export const useOpenOrderContext = () => useContext(OpenOrderContext);

const OpenOrderProvider = ({ children, storeNumber }) => {
  const dispatch = useDispatch();
  const [openOrderId, setOpenOrderId, clearOpenOrderId] = useWindowStorage(
    getStoreOpenOrderNumberKey(storeNumber),
    "",
  );

  const storeSpecificPoints = useSelector(getStoreBalance);
  const initStoreSpecificPoints = useSelector(getStoreInitBalance);
  const isOrganizerShareAdded = useSelector(getIsOrganizerShareAdded);
  const cartOrder = useSelector(getCartOrder);
  const isRejectionProcessingCompleted = useSelector(getIsRejectionProcessingCompleted);

  //for open orders add organiser share in store specific points
  useEffect(() => {
    if (storeNumber && openOrderId && cartOrder?.organizerShare) {
      if (cartOrder.state === ORDER_STATE_CART) {
        const balance = storeSpecificPoints + +(cartOrder?.organizerShare?.amount || 0);
        !isOrganizerShareAdded &&
          dispatch(setStoreBalance({ balance, isOrganizerShareAdded: true }));
      } else {
        resetOpenOrder();
      }
    }
  }, [storeNumber, openOrderId, cartOrder, isOrganizerShareAdded]);

  useEffect(() => {
    if (isRejectionProcessingCompleted) {
      resetOpenOrder();
    }
  }, [isRejectionProcessingCompleted]);

  //to handle scenario when open order id exists in local storage but user enters in self order flow (country change in open order flow)
  useEffect(() => {
    if (isOrganizerShareAdded && !cartOrder?.organizerShare) {
      resetOpenOrder();
    }
  }, [isOrganizerShareAdded, cartOrder]);

  const resetOpenOrder = useCallback(() => {
    dispatch(setStoreBalance({ balance: initStoreSpecificPoints, isOrganizerShareAdded: false }));
    clearOpenOrderId();
  }, [clearOpenOrderId, initStoreSpecificPoints]);

  const value = useMemo(
    () => ({
      resetOpenOrder,
      openOrderId,
      setOpenOrderId,
    }),
    [resetOpenOrder, openOrderId, setOpenOrderId],
  );

  return <OpenOrderContext.Provider value={value}>{children}</OpenOrderContext.Provider>;
};

export default OpenOrderProvider;
